import { Timestamp, doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";

export type YoutubeConfig = {
  aboutUsVideos: string[];
  videos: string[];
  version?: number;
  updatedAt?: string;
};

export type IrConfigItem = {
  code: string;
  name: string;
  title: string;
  fileUrl: string;
  fileName?: string;
  publishedAt?: string;
};

export type IrConfig = {
  items: IrConfigItem[];
  version?: number;
};

export type HistoryConfigItem = {
  id?: string;
  sort?: number;
  year: string;
  contents: string[];
};

export type HistoryConfig = {
  items: HistoryConfigItem[];
  version?: number;
  updatedAt?: string;
};

export type PopupAlignH = "left" | "center" | "right";
export type PopupAlignV = "top" | "center" | "bottom";

export type PopupConfigItem = {
  id: string;
  imageUrl: string;
  alt?: string;
  enabled: boolean;
  linkUrl?: string;
  linkTarget?: "_blank" | "_self";
  hAlign?: PopupAlignH;
  vAlign?: PopupAlignV;
  offsetX?: number;
  offsetY?: number;
  startAt?: string;
  endAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PopupConfig = {
  popups: PopupConfigItem[];
  version?: number;
  updatedAt?: string;
};

const FALLBACK_ABOUTUS_VIDEOS = [
  "https://www.youtube.com/embed/gTB9qSvwlws?si=LLxKSHDIlr9QpKOm",
  "https://www.youtube.com/embed/HQcWYF4j-ic?si=IJfw0fOqq65XW0lq",
  "https://www.youtube.com/embed/WdLMyqNUz4M?si=vSR4InNG0d3RW7_Z",
  "https://www.youtube.com/embed/nq4xTqmr4oQ?si=1CdrSD1kGMI6UMut",
];

const FALLBACK_VIDEOS = [
  "https://www.youtube.com/embed/gTB9qSvwlws?si=LLxKSHDIlr9QpKOm",
  "https://www.youtube.com/embed/WWVazpPB3W8?si=-I2yvxTVJsOqXoKh",
  "https://www.youtube.com/embed/Wene5E5Wt7I?si=3nU7bw40jbkYIB4H",
];

const FALLBACK_IR_ITEMS: IrConfigItem[] = [
  {
    code: "ir_en",
    name: "IR EN",
    title: "Download IR Deck",
    fileUrl: "/ir/THE_ENGINEER_IR.pdf",
    fileName: "THE_ENGINEER_IR.pdf",
    publishedAt: undefined,
  },
  {
    code: "ir_kr",
    name: "IR KR",
    title: "IR 자료 다운로드",
    fileUrl: "/ir/디엔지니어_IR.pdf",
    fileName: "디엔지니어_IR.pdf",
    publishedAt: undefined,
  },
];

const FALLBACK_HISTORY_ITEMS: HistoryConfigItem[] = [
  {
    id: "history-2020-07",
    sort: 49,
    year: "2020.07",
    contents: [
      "The Engineer 설립",
      "The Engineer 상표 출원 (2021.03.24 기준 심사 중)",
      "스마트제조혁신추진단 공급기업 등록",
    ],
  },
  {
    id: "history-2020-12",
    sort: 48,
    year: "2020.12",
    contents: [
      "카카오톡 오픈채팅방 개설",
      "엔지니어 구인구직 네트워크",
      "인원 1200명 이상 유지 중(현시점기준)",
      "매출 5억 달성",
      "2차전지, 디스플레이, 반도체 관련 장비 업체",
      "5군데 협력 업체 등록",
    ],
  },
  {
    id: "history-2021-03",
    sort: 47,
    year: "2021.03",
    contents: ["The Engineer 홈페이지 개설", "The Engineer 네이버 블로그 개설"],
  },
  {
    id: "history-2021-04",
    sort: 46,
    year: "2021.04",
    contents: ["에스피투로보틱스 MOU체결"],
  },
  {
    id: "history-2026-03",
    sort: 1,
    year: "2026.03",
    contents: [
      "산업디자인전문회사 승인",
      "인공지능 기반 다차원 디자인 시안 및 가상 프로토타입 자동 생성 프로그램 저작권 등록",
      "AI바우처 공급기업 선정",
    ],
  },
];

export const FALLBACK_YOUTUBE_CONFIG: YoutubeConfig = {
  aboutUsVideos: FALLBACK_ABOUTUS_VIDEOS,
  videos: FALLBACK_VIDEOS,
  version: undefined,
  updatedAt: undefined,
};

export const FALLBACK_IR_CONFIG: IrConfig = {
  items: FALLBACK_IR_ITEMS,
  version: undefined,
};

export const FALLBACK_HISTORY_CONFIG: HistoryConfig = {
  items: FALLBACK_HISTORY_ITEMS,
  version: undefined,
  updatedAt: undefined,
};

export const FALLBACK_POPUP_CONFIG: PopupConfig = {
  popups: [],
  version: undefined,
  updatedAt: undefined,
};

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string" && item.trim().length > 0);
}

function asOptionalString(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim().length > 0) return value;
  return undefined;
}

function asOptionalNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return undefined;
}

function asOptionalBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  return undefined;
}

function normalizeToken(value: string): string {
  return value.toLowerCase().replace(/[\s_-]/g, "");
}

function normalizeDateLike(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim().length > 0) return value;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  return undefined;
}

function parseYoutubeConfig(data: unknown): YoutubeConfig | null {
  if (!data || typeof data !== "object") return null;
  const source = data as Record<string, unknown>;

  const aboutUsVideos = isStringArray(source.about_us) ? source.about_us : FALLBACK_YOUTUBE_CONFIG.aboutUsVideos;
  const videos = isStringArray(source.videos) ? source.videos : FALLBACK_YOUTUBE_CONFIG.videos;

  return {
    aboutUsVideos,
    videos,
    version: asOptionalNumber(source.version),
    updatedAt: normalizeDateLike(source.updatedAt),
  };
}

function parseIrItem(value: unknown): IrConfigItem | null {
  if (!value || typeof value !== "object") return null;
  const source = value as Record<string, unknown>;

  const code = asOptionalString(source.code) || asOptionalString(source.id) || "";
  const name = asOptionalString(source.name) || "";
  const title = asOptionalString(source.title) || "";
  const fileUrl = asOptionalString(source.file_url) || asOptionalString(source.static_url);
  const fileName = asOptionalString(source.file_name) || asOptionalString(source.name);
  const publishedAt = normalizeDateLike(source.published_at);

  if (!fileUrl) return null;

  return { code, name, title, fileUrl, fileName, publishedAt };
}

function parseIrConfig(data: unknown): IrConfig | null {
  if (!data || typeof data !== "object") return null;
  const source = data as Record<string, unknown>;

  const parsedItems = Array.isArray(source.items) ? source.items.map(parseIrItem).filter(Boolean) : [];
  const items = parsedItems.length > 0 ? (parsedItems as IrConfigItem[]) : FALLBACK_IR_CONFIG.items;

  return {
    items,
    version: asOptionalNumber(source.version),
  };
}

function parseYearToComparableValue(year: string): number {
  const match = year.trim().match(/^(\d{4})(?:[.\-/]?(\d{1,2}))?/);
  if (!match) return Number.MAX_SAFE_INTEGER;

  const parsedYear = Number(match[1]);
  const parsedMonth = Number(match[2] || "1");

  if (!Number.isFinite(parsedYear)) return Number.MAX_SAFE_INTEGER;
  if (!Number.isFinite(parsedMonth)) return parsedYear * 100 + 1;

  const month = Math.min(12, Math.max(1, parsedMonth));
  return parsedYear * 100 + month;
}

function sortHistoryItemsAscending(items: HistoryConfigItem[]): HistoryConfigItem[] {
  const everyItemHasSort = items.length > 0 && items.every((item) => typeof item.sort === "number");
  if (everyItemHasSort) {
    return [...items].sort((a, b) => (b.sort as number) - (a.sort as number));
  }

  return [...items].sort((a, b) => parseYearToComparableValue(a.year) - parseYearToComparableValue(b.year));
}

function parseHistoryItem(value: unknown): HistoryConfigItem | null {
  if (!value || typeof value !== "object") return null;
  const source = value as Record<string, unknown>;

  const year = asOptionalString(source.year);
  if (!year) return null;

  const contents = Array.isArray(source.contents)
    ? source.contents.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)
    : [];
  if (contents.length === 0) return null;

  return {
    id: asOptionalString(source.id),
    sort: asOptionalNumber(source.sort),
    year,
    contents,
  };
}

function parseHistoryConfig(data: unknown): HistoryConfig | null {
  if (!data || typeof data !== "object") return null;
  const source = data as Record<string, unknown>;

  const parsedItems = Array.isArray(source.items) ? source.items.map(parseHistoryItem).filter(Boolean) : [];
  const items =
    parsedItems.length > 0
      ? sortHistoryItemsAscending(parsedItems as HistoryConfigItem[])
      : sortHistoryItemsAscending(FALLBACK_HISTORY_CONFIG.items);

  return {
    items,
    version: asOptionalNumber(source.version),
    updatedAt: normalizeDateLike(source.updatedAt),
  };
}

function parsePopupAlignH(value: unknown): PopupAlignH | undefined {
  if (value === "left" || value === "center" || value === "right") return value;
  return undefined;
}

function parsePopupAlignV(value: unknown): PopupAlignV | undefined {
  if (value === "top" || value === "center" || value === "bottom") return value;
  return undefined;
}

function parsePopupItem(value: unknown, index: number): PopupConfigItem | null {
  if (!value || typeof value !== "object") return null;
  const source = value as Record<string, unknown>;

  const imageUrl = asOptionalString(source.image_url) || asOptionalString(source.static_url);
  if (!imageUrl) return null;

  const rawTarget = asOptionalString(source.link_target);
  const linkTarget = rawTarget === "_self" ? "_self" : rawTarget === "_blank" ? "_blank" : undefined;

  return {
    id: asOptionalString(source.id) || `popup-${index + 1}`,
    imageUrl,
    alt: asOptionalString(source.alt),
    enabled: asOptionalBoolean(source.enabled) ?? true,
    linkUrl: asOptionalString(source.link_url),
    linkTarget,
    hAlign: parsePopupAlignH(source.hAlign),
    vAlign: parsePopupAlignV(source.vAlign),
    offsetX: asOptionalNumber(source.offsetX),
    offsetY: asOptionalNumber(source.offsetY),
    startAt: normalizeDateLike(source.start_at),
    endAt: normalizeDateLike(source.end_at),
    createdAt: normalizeDateLike(source.created_at),
    updatedAt: normalizeDateLike(source.updated_at),
  };
}

function parsePopupConfig(data: unknown): PopupConfig | null {
  if (!data || typeof data !== "object") return null;
  const source = data as Record<string, unknown>;

  const popups = Array.isArray(source.popups)
    ? (source.popups
        .map((item, index) => parsePopupItem(item, index))
        .filter(Boolean) as PopupConfigItem[])
    : [];

  return {
    popups,
    version: asOptionalNumber(source.version),
    updatedAt: normalizeDateLike(source.updatedAt),
  };
}

function cloneYoutubeConfig(config: YoutubeConfig): YoutubeConfig {
  return {
    aboutUsVideos: [...config.aboutUsVideos],
    videos: [...config.videos],
    version: config.version,
    updatedAt: config.updatedAt,
  };
}

function cloneIrConfig(config: IrConfig): IrConfig {
  return {
    items: config.items.map((item) => ({ ...item })),
    version: config.version,
  };
}

function cloneHistoryConfig(config: HistoryConfig): HistoryConfig {
  return {
    items: config.items.map((item) => ({
      ...item,
      contents: [...item.contents],
    })),
    version: config.version,
    updatedAt: config.updatedAt,
  };
}

function clonePopupConfig(config: PopupConfig): PopupConfig {
  return {
    popups: config.popups.map((popup) => ({ ...popup })),
    version: config.version,
    updatedAt: config.updatedAt,
  };
}

const IR_LANG_RULES = {
  en: {
    codes: ["ir_en", "en"],
    hints: ["english", "en", "download ir deck", "ir en"],
  },
  ko: {
    codes: ["ir_kr", "ir_ko", "kr", "ko"],
    hints: ["korean", "ko", "kr", "국문", "한글", "자료 다운로드"],
  },
};

function scoreIrItem(item: IrConfigItem, lang: "en" | "ko"): number {
  const rules = IR_LANG_RULES[lang];
  const code = normalizeToken(item.code);
  const haystack = normalizeToken([item.code, item.name, item.title, item.fileName].join(" "));
  let score = 0;

  if (rules.codes.some((target) => code === normalizeToken(target))) {
    score += 100;
  }
  if (rules.codes.some((target) => code.includes(normalizeToken(target)))) {
    score += 40;
  }

  for (const hint of rules.hints) {
    if (haystack.includes(normalizeToken(hint))) {
      score += 10;
    }
  }

  if (lang === "en" && (haystack.includes("korean") || haystack.includes("국문") || haystack.includes("한글"))) {
    score -= 20;
  }
  if (lang === "ko" && (haystack.includes("english") || haystack.includes("downloadirdeck"))) {
    score -= 20;
  }

  return score;
}

export type IrTargets = {
  en: IrConfigItem | null;
  ko: IrConfigItem | null;
};

export function selectIrTargets(items: IrConfigItem[]): IrTargets {
  if (items.length === 0) {
    return { en: null, ko: null };
  }

  const enScored = [...items]
    .map((item) => ({ item, score: scoreIrItem(item, "en") }))
    .sort((a, b) => b.score - a.score);
  const en = enScored[0]?.item || items[0];

  const koCandidates = items.filter((item) => item.fileUrl !== en.fileUrl);
  const koBase = koCandidates.length > 0 ? koCandidates : items;
  const koScored = [...koBase]
    .map((item) => ({ item, score: scoreIrItem(item, "ko") }))
    .sort((a, b) => b.score - a.score);
  const ko = koScored[0]?.item || (items.length > 1 ? items[1] : items[0]);

  if (en && ko && en.fileUrl === ko.fileUrl) {
    console.warn("[cms] IR EN/KR resolved to same file_url:", en.fileUrl);
  }

  return { en: en || null, ko: ko || null };
}

export async function fetchYoutubeConfig(): Promise<YoutubeConfig> {
  try {
    const snapshot = await getDoc(doc(db, "site_config", "youtube"));
    if (!snapshot.exists()) {
      console.warn("[cms] site_config/youtube missing. Fallback will be used.");
      return cloneYoutubeConfig(FALLBACK_YOUTUBE_CONFIG);
    }

    const parsed = parseYoutubeConfig(snapshot.data());
    if (!parsed) {
      console.warn("[cms] site_config/youtube schema invalid. Fallback will be used.");
      return cloneYoutubeConfig(FALLBACK_YOUTUBE_CONFIG);
    }

    return parsed;
  } catch (error) {
    console.warn("[cms] failed to read site_config/youtube. Fallback will be used.", error);
    return cloneYoutubeConfig(FALLBACK_YOUTUBE_CONFIG);
  }
}

export async function fetchIrConfig(): Promise<IrConfig> {
  try {
    const snapshot = await getDoc(doc(db, "site_config", "ir"));
    if (!snapshot.exists()) {
      console.warn("[cms] site_config/ir missing. Fallback will be used.");
      return cloneIrConfig(FALLBACK_IR_CONFIG);
    }

    const parsed = parseIrConfig(snapshot.data());
    if (!parsed) {
      console.warn("[cms] site_config/ir schema invalid. Fallback will be used.");
      return cloneIrConfig(FALLBACK_IR_CONFIG);
    }

    return parsed;
  } catch (error) {
    console.warn("[cms] failed to read site_config/ir. Fallback will be used.", error);
    return cloneIrConfig(FALLBACK_IR_CONFIG);
  }
}

export async function fetchHistoryConfig(): Promise<HistoryConfig> {
  try {
    const snapshot = await getDoc(doc(db, "site_config", "history"));
    if (!snapshot.exists()) {
      console.warn("[cms] site_config/history missing. Fallback will be used.");
      return cloneHistoryConfig(FALLBACK_HISTORY_CONFIG);
    }

    const parsed = parseHistoryConfig(snapshot.data());
    if (!parsed) {
      console.warn("[cms] site_config/history schema invalid. Fallback will be used.");
      return cloneHistoryConfig(FALLBACK_HISTORY_CONFIG);
    }

    return parsed;
  } catch (error) {
    console.warn("[cms] failed to read site_config/history. Fallback will be used.", error);
    return cloneHistoryConfig(FALLBACK_HISTORY_CONFIG);
  }
}

export async function fetchPopupConfig(): Promise<PopupConfig> {
  try {
    const snapshot = await getDoc(doc(db, "site_config", "popup"));
    if (!snapshot.exists()) {
      console.warn("[cms] site_config/popup missing. Fallback will be used.");
      return clonePopupConfig(FALLBACK_POPUP_CONFIG);
    }

    const parsed = parsePopupConfig(snapshot.data());
    if (!parsed) {
      console.warn("[cms] site_config/popup schema invalid. Fallback will be used.");
      return clonePopupConfig(FALLBACK_POPUP_CONFIG);
    }

    return parsed;
  } catch (error) {
    console.warn("[cms] failed to read site_config/popup. Fallback will be used.", error);
    return clonePopupConfig(FALLBACK_POPUP_CONFIG);
  }
}
